import { useState, useEffect, useMemo } from "react";
import ButtonGroup from "@mui/material/ButtonGroup";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import { DatePicker } from "@mui/x-date-pickers-pro";
import { DataGridPro } from "@mui/x-data-grid-pro";
import dayjs from "dayjs";
import ModelAutoComplete from "../../actprep-ui/components/ModelAutoComplete";
import { useGet } from "../../actprep-ui/hooks/api";
import { apiUrls } from "../../actprep-ui/utils/urls";
import FullScreenDialog from "../../actprep-ui/components/FullScreenDialog";
import { dataGridNestedColumnGetter } from "../../actprep-ui/utils/tables";
import api from "../../actprep-ui/utils/api";
import { showSuccessSnackbar } from "../../actprep-ui/utils/feedback";
import { useConfirm } from "material-ui-confirm";

export default function TransferStudentsForm({ isOpen, onClose }) {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    source_role_assignment: null,
    target_role_assignment: null,
    transfer_date: dayjs(),
  });
  const confirm = useConfirm();
  const [asOfDate, setAsOfDate] = useState(dayjs());
  const [errors, setErrors] = useState({});
  const [selectedSourceRoleAssignment, setSelectedSourceRoleAssignment] =
    useState(null);
  const [selectedTargetRoleAssignment, setSelectedTargetRoleAssignment] =
    useState(null);
  const [selectedStudentModel, setSelectedStudentModel] = useState({
    ids: new Set(),
    type: "include",
  });
  const { data: affectedStudentsData } = useGet(
    apiUrls.coaching.studentToCoachLink(),
    {
      enabled: !!selectedSourceRoleAssignment,
      params: {
        values: ["uuid", "student__user", "student__cohort__event_name"],
        search_params: {
          role_assignment_id: selectedSourceRoleAssignment?.uuid,
          is_anchor: true,
          as_of: asOfDate.format("YYYY-MM-DD"),
        },
        size: 500,
        keep_related: true,
        select_related: ["student__cohort", "student__user"],
        order_by: ["student__cohort__start_date"],
      },
    }
  );
  const totalCount = affectedStudentsData?.total_count ?? 0;
  const affectedStudents = useMemo(
    () => affectedStudentsData?.data || [],
    [affectedStudentsData]
  );

  // Calculate valid date range based on both source and target role assignments
  const validDateRange = useMemo(() => {
    if (!selectedSourceRoleAssignment || !selectedTargetRoleAssignment)
      return null;

    const sourceStart = dayjs(selectedSourceRoleAssignment.data.start_date);
    const sourceEnd = selectedSourceRoleAssignment.data?.end_date
      ? dayjs(selectedSourceRoleAssignment.data.end_date)
      : null;
    const targetStart = dayjs(selectedTargetRoleAssignment.data.start_date);
    const targetEnd = selectedTargetRoleAssignment.data?.end_date
      ? dayjs(selectedTargetRoleAssignment.data.end_date)
      : null;

    // Transfer date must be at least one day after source start date
    const minDate = sourceStart.add(1, "day");
    // Also must be within target role assignment window
    const effectiveMinDate = minDate.isAfter(targetStart, "day")
      ? minDate
      : targetStart;

    // Max date is the earliest of source end, target end, or null (ongoing)
    let effectiveMaxDate = null;
    if (sourceEnd && targetEnd) {
      effectiveMaxDate = sourceEnd.isBefore(targetEnd) ? sourceEnd : targetEnd;
    } else if (sourceEnd) {
      effectiveMaxDate = sourceEnd;
    } else if (targetEnd) {
      effectiveMaxDate = targetEnd;
    }

    return {
      minDate: effectiveMinDate,
      maxDate: effectiveMaxDate,
      startDate: effectiveMinDate,
      endDate: effectiveMaxDate,
    };
  }, [selectedSourceRoleAssignment, selectedTargetRoleAssignment]);

  // Calculate valid as-of date range based on selected source role assignment
  const sourceValidDateRange = useMemo(() => {
    if (!selectedSourceRoleAssignment) return null;

    const roleStart = dayjs(selectedSourceRoleAssignment.data.start_date);
    const roleEnd = selectedSourceRoleAssignment.data?.end_date
      ? dayjs(selectedSourceRoleAssignment.data.end_date)
      : null;

    return {
      minDate: roleStart,
      maxDate: roleEnd,
      startDate: roleStart,
      endDate: roleEnd,
    };
  }, [selectedSourceRoleAssignment]);

  // Keep selected students in sync with latest affected students list
  useEffect(() => {
    if (!affectedStudents || affectedStudents.length === 0) {
      setSelectedStudentModel({
        ids: new Set(),
        type: "include",
      });
      return;
    }
    setSelectedStudentModel({
      ids: new Set(),
      type: "exclude",
    });
  }, [affectedStudents]);

  // Validate form data
  const validateForm = () => {
    const newErrors = {};

    if (!formData.source_role_assignment) {
      newErrors.source_role_assignment = "Source role assignment is required";
    }

    if (!formData.target_role_assignment) {
      newErrors.target_role_assignment = "Target role assignment is required";
    }

    if (!formData.transfer_date) {
      newErrors.transfer_date = "Transfer date is required";
    }

    // Check if transfer date is at least one day after source start and within target window
    if (
      selectedSourceRoleAssignment &&
      selectedTargetRoleAssignment &&
      formData.transfer_date
    ) {
      const sourceStart = dayjs(selectedSourceRoleAssignment.data.start_date);
      const targetStart = dayjs(selectedTargetRoleAssignment.data.start_date);
      const targetEnd = selectedTargetRoleAssignment.data.end_date
        ? dayjs(selectedTargetRoleAssignment.data.end_date)
        : null;

      // Must be at least one day after source start date
      const minAllowedDate = sourceStart.add(1, "day");
      if (formData.transfer_date.isBefore(minAllowedDate, "day")) {
        newErrors.transfer_date = `Transfer date must be at least one day after source role assignment start date (${minAllowedDate.format(
          "MM/DD/YYYY"
        )})`;
      }

      // Must be within target role assignment window
      if (formData.transfer_date.isBefore(targetStart, "day")) {
        newErrors.transfer_date = `Transfer date must be on or after target role assignment start date (${targetStart.format(
          "MM/DD/YYYY"
        )})`;
      }

      if (targetEnd && formData.transfer_date.isAfter(targetEnd, "day")) {
        newErrors.transfer_date = `Transfer date must be on or before target role assignment end date (${targetEnd.format(
          "MM/DD/YYYY"
        )})`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) {
      return;
    }
    setErrors({});
    let targetStudentLinkIds = new Set(
      affectedStudents.map(({ uuid }) => uuid)
    );
    if (selectedStudentModel.type == "exclude") {
      targetStudentLinkIds = targetStudentLinkIds.difference(
        selectedStudentModel.ids
      );
    } else {
      targetStudentLinkIds = selectedStudentModel.ids;
    }

    const isConfirmed = await new Promise((resolve) =>
      confirm({
        title: "Are you sure?",
        content: (
          <Box component={"div"}>
            <Typography sx={{ pb: 2 }}>
              Are you sure you want to transfer{" "}
              <strong>{targetStudentLinkIds.size}</strong> students from{" "}
              <strong>{formData.source_role_assignment.label}</strong> to{" "}
              <strong>{formData.target_role_assignment.label}</strong> starting
              on{" "}
              <strong>
                {formData.transfer_date.toDate().toLocaleDateString()}
              </strong>
              ?{" "}
            </Typography>
            <Typography color="error">
              WARNING: This action is not easily irreversible. Please closely
              verify all information before continuing.
            </Typography>
          </Box>
        ),
      })
        .then(() => resolve(true))
        .catch(() => resolve(false))
    );
    if (!isConfirmed) {
      return;
    }

    setIsLoading(true);

    try {
      const payload = {
        student_link_ids: Array.from(targetStudentLinkIds),
        source_role_assignment_id: formData.source_role_assignment.uuid,
        target_role_assignment_id: formData.target_role_assignment.uuid,
        transfer_date: formData.transfer_date.format("YYYY-MM-DD"),
      };
      const response = await api.post(
        apiUrls.coaching.transferStudents(),
        payload
      );
      if (response.data?.success) {
        showSuccessSnackbar(
          `${targetStudentLinkIds.size} Students Transferred!`
        );
        onClose();
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const handleReset = () => {
    setFormData({
      source_role_assignment: null,
      target_role_assignment: null,
      transfer_date: dayjs(),
    });
    setSelectedSourceRoleAssignment(null);
    setSelectedTargetRoleAssignment(null);
    setErrors({});
  };

  const updateFormValue = (field, newValue) => {
    setFormData((prev) => ({
      ...prev,
      [field]: newValue,
    }));

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const handleSourceRoleAssignmentChange = (newValue) => {
    setSelectedSourceRoleAssignment(newValue);
    updateFormValue("source_role_assignment", newValue || null);

    // When source role changes, reset as-of date within the valid window
    if (newValue?.data?.start_date) {
      const start = dayjs(newValue.data.start_date);
      const end = newValue.data.end_date ? dayjs(newValue.data.end_date) : null;
      let nextAsOf = dayjs();

      if (nextAsOf.isBefore(start, "day")) {
        nextAsOf = start;
      }

      if (end && nextAsOf.isAfter(end, "day")) {
        nextAsOf = end;
      }

      setAsOfDate(nextAsOf);
    }
  };

  const handleTargetRoleAssignmentChange = (newValue) => {
    setSelectedTargetRoleAssignment(newValue);
    updateFormValue("target_role_assignment", newValue || null);

    // Reset transfer date when target role assignment changes
    setFormData((prev) => ({
      ...prev,
      transfer_date: dayjs(),
    }));
  };

  const handleTransferDateChange = (newDate) => {
    updateFormValue("transfer_date", newDate);
  };

  const handleAsOfDateChange = (newDate) => {
    if (!newDate) {
      setAsOfDate(dayjs());
      return;
    }
    setAsOfDate(newDate);
  };

  // Initialize form when dialog opens
  useEffect(() => {
    if (isOpen) {
      handleReset();
    }
  }, [isOpen]);

  const numAffectedStudents =
    selectedStudentModel.type == "exclude"
      ? affectedStudents.length - selectedStudentModel.ids.size
      : selectedStudentModel.ids.size;

  return (
    <FullScreenDialog
      title={
        <Box>
          <Typography variant="h6" component="div">
            Transfer Students
          </Typography>
          <Typography variant="body2">
            Transfer students from one coach role assignment to another
          </Typography>
        </Box>
      }
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      component="form"
      onSubmit={handleSubmit}
    >
      <Grid container spacing={2} sx={{ mt: 1 }}>
        {/* Source Role Assignment Selection */}
        <Grid size={12}>
          <ModelAutoComplete
            modelName="CoachRoleAssignment"
            label="Source Role Assignment"
            value={selectedSourceRoleAssignment}
            setValue={handleSourceRoleAssignmentChange}
            error={!!errors.source_role_assignment}
            TextFieldProps={{
              error: !!errors.source_role_assignment,
              helperText: errors.source_role_assignment,
            }}
            includeFields={["start_date", "end_date"]}
          />
        </Grid>

        {/* Target Role Assignment Selection */}
        <Grid size={12}>
          <ModelAutoComplete
            modelName="CoachRoleAssignment"
            label="Target Role Assignment"
            value={selectedTargetRoleAssignment}
            setValue={handleTargetRoleAssignmentChange}
            error={!!errors.target_role_assignment}
            TextFieldProps={{
              error: !!errors.target_role_assignment,
              helperText: errors.target_role_assignment,
            }}
            includeFields={["start_date", "end_date"]}
          />
        </Grid>

        {/* Transfer Date */}
        <Grid size={12}>
          {/* <Typography variant="body2" sx={{ mb: 1 }}>
              Transfer Date
            </Typography> */}
          {validDateRange && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Valid Transfer Date Range:</strong>{" "}
                {validDateRange.startDate.format("MM/DD/YYYY")}
                {validDateRange.endDate
                  ? ` to ${validDateRange.endDate.format("MM/DD/YYYY")}`
                  : " to ongoing"}
              </Typography>
            </Alert>
          )}
          <DatePicker
            label="Transfer Date"
            value={formData.transfer_date}
            onChange={handleTransferDateChange}
            minDate={validDateRange?.minDate}
            maxDate={validDateRange?.maxDate}
            slotProps={{
              textField: {
                size: "small",
                variant: "standard",
                error: !!errors.transfer_date,
                helperText: errors.transfer_date,
                fullWidth: true,
              },
            }}
          />
        </Grid>

        {/* As Of Date & Affected Students */}
        {selectedSourceRoleAssignment && (
          <>
            <Grid size={12}>
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                Affected Students ({numAffectedStudents} of {totalCount}{" "}
                selected)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                The <strong>As Of</strong> date controls which students are
                considered affected based on their coaching relationship on that
                date. Only students anchored to the source role assignment on
                the selected date will be included.
              </Typography>
            </Grid>
            <Grid
              size={12}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography variant="body2">As Of Date</Typography>
              <DatePicker
                value={asOfDate}
                onChange={handleAsOfDateChange}
                minDate={sourceValidDateRange?.minDate}
                maxDate={sourceValidDateRange?.maxDate}
                slotProps={{
                  textField: {
                    size: "small",
                    variant: "outlined",
                  },
                }}
              />
            </Grid>
            <Grid size={12}>
              <Box sx={{ height: 400, width: "100%" }}>
                <DataGridPro
                  rows={affectedStudents}
                  getRowId={(row) => row.uuid}
                  columns={[
                    {
                      field: "student",
                      headerName: "Student",
                      flex: 1,
                      valueGetter: (student) => {
                        const user = student?.user ?? {};
                        const first = user.first_name || "";
                        const last = user.last_name || "";
                        return `${first} ${last}`.trim() || "Unknown Student";
                      },
                    },
                    {
                      field: "student.cohort.event_name",
                      headerName: "Cohort",
                      valueGetter: dataGridNestedColumnGetter,
                      flex: 1,
                    },
                  ]}
                  checkboxSelection
                  disableRowSelectionOnClick
                  rowSelectionModel={selectedStudentModel}
                  onRowSelectionModelChange={(newModel) => {
                    setSelectedStudentModel(newModel);
                  }}
                  density="compact"
                />
              </Box>
            </Grid>
          </>
        )}
      </Grid>
      <ButtonGroup fullWidth disableElevation sx={{ p: 2 }}>
        <LoadingButton onClick={handleCancel} disabled={isLoading}>
          Cancel
        </LoadingButton>
        <LoadingButton loading={isLoading} disabled={isLoading} type="submit">
          Transfer
        </LoadingButton>
      </ButtonGroup>
    </FullScreenDialog>
  );
}
